import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import ImageInput from "@/components/ImageInput/Index";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import getBannerIntialValues from "@/data/ContentManagement/Banners/initial-values";
import { bannersApi, languagesApi, sportsMasterDataApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import BannerSchema from "@/schema/ContentManagement/Banners/BannerSchema";
import SelectInput from "@/components/SelectInput/SelectInput";
import { bannerPageValues } from "@/data/ContentManagement/Banners/page-values";

const BannerModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const showApi = modalState.id ? `${bannersApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Banner ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${bannersApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: bannersApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getBannerIntialValues(modalState.id ? showData : null),
    validationSchema: BannerSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData: !!modalState?.id && showData?.data?.sport,
  });
  const allSportOption = [
    { label: "SP-All", value: "all" },
    ...sportDefaultOptions,
  ];

  const languageListApi = `${languagesApi}${baseQueryParams}`;
  const {
    defaultOptions: defaultLanguageDefaultOptions,
    debouncedFetchOptions: defaultLanguageFetchOptions,
    loading: defaultLanguageLoading,
    getSelectedOption: defaultLanguageGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: languageListApi,
    formikValue: formik.values.language_id,
    formikInitialValue: formik.initialValues.language_id,
    showData: !!modalState?.id && showData?.data?.language,
  });

  const allLanguageOption = [
    { label: "All", value: "all" },
    { label: "All-EN", value: "all-en" },
    ...defaultLanguageDefaultOptions.slice(1),
  ];

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) {
      formData.append("_method", "PUT");
    }
    formData.append("name", values.name);
    formData.append("page", values.page);

    formData.append("sport_id", values.sport_id);
    formData.append("language_id", values.language_id);

    formData.append("url_redirect", values.url_redirect);
    formData.append("status", values.status);

    if (values.image_url && values.image_url instanceof File)
      formData.append("image_url", values.image_url);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Banner" : "Add Banner"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Name"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <SelectInput
            label="Page"
            placeholder="No page selected."
            defaultOptions={bannerPageValues}
            value={
              bannerPageValues.find(
                (option) => option.value === formik.values.page
              ) || null
            }
            onChange={(selected) =>
              formik.setFieldValue("page", selected.value)
            }
            errorText={
              (formik.touched.page && formik.errors.page) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.page?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport"
            placeholder="No sport selected. Type to search..."
            defaultOptions={allSportOption}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={
              allSportOption.find(
                (option) => option.value === formik.values.sport_id
              ) || null
            }
            onChange={(selected) => {
              formik.setFieldValue("sport_id", selected?.value || "all");
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Default Language"
            placeholder="No default language selected. Type to search..."
            defaultOptions={allLanguageOption}
            loadOptions={defaultLanguageFetchOptions}
            isLoading={defaultLanguageLoading}
            value={
              allLanguageOption.find(
                (option) => option.value === formik.values.language_id
              ) || null
            }
            onChange={(selected) => {
              formik.setFieldValue("language_id", selected?.value || "all");
            }}
            errorText={
              (formik.touched.language_id && formik.errors.language_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.language_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"url_redirect"}
            saveError={saveError}
            label="URL Redirect"
            required={false}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ImageInput
            formFormik={formik}
            fieldName="image_url"
            label="Image Url"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.image_url}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default BannerModal;
